import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/dahfs9roh653ivfvntog';
const MAX_DETAIL = 500;

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function ReportForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [detail, setDetail] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    // Anti-spam honeypot: silently treat as success without sending anything.
    const honeypot = String(payload.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      form.reset();
      setDetail('');
      return;
    }

    setStatus('submitting');
    setFormError('');

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim() === '') return;
      body.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (!ok) {
        setStatus('error');
        setFormError(
          isSpam
            ? '提交未通过校验，请检查内容后重新提交。'
            : serverMsg || '提交失败，请稍后重试，或直接发邮件给我们。'
        );
        return;
      }

      setStatus('success');
      setFormError('');
      form.reset();
      setDetail('');
    } catch {
      setStatus('error');
      setFormError('网络异常，暂时无法提交。请稍后重试，或直接发送邮件至 report@heiliaoleida.com。');
    }
  };

  return (
    <form
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="nickname" className="block text-sm font-medium text-foreground-800">
            称呼（可选）
          </label>
          <input
            id="nickname"
            name="nickname"
            type="text"
            autoComplete="off"
            placeholder="如何称呼你，可留空"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground-800">
            联系邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            autoComplete="email"
            placeholder="用于回复核实结果"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="report_type" className="block text-sm font-medium text-foreground-800">
            线索类型 <span className="text-primary-600">*</span>
          </label>
          <select
            id="report_type"
            name="report_type"
            required
            defaultValue=""
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
          >
            <option value="" disabled>请选择线索类型</option>
            <option value="数据泄露">数据泄露</option>
            <option value="安全漏洞">安全漏洞</option>
            <option value="盗刷与诈骗">盗刷与诈骗</option>
            <option value="隐私风险">隐私风险</option>
            <option value="违规收集使用">违规收集使用</option>
            <option value="监管处罚信息">监管处罚信息</option>
            <option value="内容更正">内容更正</option>
            <option value="其他">其他</option>
          </select>
        </div>
        <div>
          <label htmlFor="platform_type" className="block text-sm font-medium text-foreground-800">
            涉及平台类型（可选）
          </label>
          <input
            id="platform_type"
            name="platform_type"
            type="text"
            autoComplete="off"
            placeholder="如：社交、电商、招聘、在线教育"
            className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="detail" className="block text-sm font-medium text-foreground-800">
          详细描述 <span className="text-primary-600">*</span>
        </label>
        <textarea
          id="detail"
          name="detail"
          required
          rows={6}
          maxLength={MAX_DETAIL}
          value={detail}
          onChange={(event) => setDetail(event.target.value)}
          placeholder="请描述事情经过：大致时间、你遇到的具体现象、平台名称类型，以及是否留有截图等可核实信息。请勿在描述中填写完整的账号密码或身份证号。"
          className="mt-2 w-full resize-y rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
        />
        <p className="mt-1.5 text-right text-xs text-foreground-500">
          {detail.length} / {MAX_DETAIL}
        </p>
      </div>

      <div className="report-field-extra" aria-hidden="true">
        <label htmlFor="website_alt">备用联络方式</label>
        <input
          id="website_alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? '正在提交…' : '提交线索'}
        </button>
        <p className="text-xs leading-6 text-foreground-500">
          提交即表示你理解：我们仅将信息用于核实与内容整理，不会公开你的邮箱等身份信息。
        </p>
      </div>

      {status === 'success' && (
        <p
          role="status"
          className="mt-5 flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-900"
        >
          <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
            <i className="ri-checkbox-circle-line text-base"></i>
          </span>
          已收到你的线索，感谢补充。我们通常会在 48 小时内查看，若需要进一步核实会通过邮箱联系你。
        </p>
      )}

      {status === 'error' && (
        <p
          role="alert"
          className="mt-5 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800"
        >
          <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
            <i className="ri-error-warning-line text-base"></i>
          </span>
          {formError}
        </p>
      )}
    </form>
  );
}